import { useCallback, useState } from 'react';
import type { IObjectTableFilter } from 'components/ObjectTable/ObjectTableFilter';
import { SelectObjectsModal } from 'components/SelectObjectsModal/SelectObjectsModal';
import { isEmpty } from 'lodash';
import { fieldMappingSelector } from 'slices/field.slice';
import { organizationMapSelector } from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import { fieldFilterValues } from 'utils/fieldUtils';
import type { SyncObjectFilter } from '@timeedit/calendar-integration-api-contract';
import { EFieldType, type TType } from '@timeedit/types';
import { Button, Tag } from '@timeedit/ui-components';
import { Alert, Flex, Space } from '@timeedit/ui-components/antd';
import type { TExactSearchField } from '@timeedit/ui-components/components/ObjectFilter/ObjectFilter.type';

type SyncObjectFilterProps = {
  filter?: SyncObjectFilter;
  onChange: (filter?: SyncObjectFilter) => void;
  objectType: TType;
};

const SyncObjectFilterComponent = ({
  filter,
  objectType,
  onChange,
}: SyncObjectFilterProps) => {
  const [openSelectObjectsModal, setOpenSelectObjectsModal] = useState(false);
  const organizationMap = useAppSelector(organizationMapSelector);
  const fieldMap = useAppSelector(fieldMappingSelector);

  const onRemoveFilter = useCallback(
    (key: keyof SyncObjectFilter, itemId?: number) => {
      onChange({
        ...filter,
        [key]:
          key !== 'fields'
            ? undefined
            : filter.fields?.filter((field) => field.fieldId !== itemId),
      });
    },
    [filter, onChange]
  );

  return (
    <Space direction="vertical">
      <Space direction="vertical" style={{ width: '100%', marginBottom: 5 }}>
        {isFilterEmpty(filter) && (
          <Alert title="No filter applied, all objects will be synced." />
        )}
        {!isFilterEmpty(filter) && (
          <Flex wrap="wrap" gap="8px">
            {filter.organizations?.map((orgId) => (
              <Tag
                key={orgId}
                color="cyan"
                closable
                onClose={() => onRemoveFilter('organizations')}
              >
                Organization: {organizationMap[orgId]?.name || orgId}
              </Tag>
            ))}
            {filter.isActive !== undefined && filter.isActive !== null && (
              <Tag
                color={filter.isActive ? 'green' : 'red'}
                closable
                onClose={() => onRemoveFilter('isActive')}
              >
                {filter.isActive
                  ? 'Only active objects'
                  : 'Only inactive objects'}
              </Tag>
            )}
            {filter.fields
              ?.filter((field) => field.values.length > 0)
              .map((field) => (
                <Tag
                  color="blue"
                  key={field.fieldId + field.values.join(',')}
                  closable
                  onClose={() => onRemoveFilter('fields', field.fieldId)}
                >
                  {fieldFilterValues(field, fieldMap)}
                </Tag>
              ))}
          </Flex>
        )}
      </Space>
      <Button
        size="small"
        type="primary"
        onClick={() => setOpenSelectObjectsModal(true)}
      >
        Adjust filter
      </Button>
      <SelectObjectsModal
        availableObjectTypes={[objectType]}
        open={openSelectObjectsModal}
        onClose={() => setOpenSelectObjectsModal(false)}
        selectFilter
        defaultFilter={{
          organization: filter?.organizations?.[0],
          active: filter?.isActive,
          fields: filter?.fields,
        }}
        onFilterChange={(filter: IObjectTableFilter) => {
          onChange({
            organizations: filter?.organization && [filter.organization],
            isActive: filter?.active,
            fields: toSyncObjectFields(filter?.fields, fieldMap),
          });
          setOpenSelectObjectsModal(false);
        }}
      />
    </Space>
  );
};

export default SyncObjectFilterComponent;

const toSyncObjectFields = (
  fields: TExactSearchField[] | undefined,
  fieldMap: Record<number | string, { fieldType?: string }>
) =>
  fields?.map((field) => {
    const isCheckbox =
      fieldMap[field.fieldId]?.fieldType === EFieldType.CHECKBOX;
    return {
      fieldId: Number(field.fieldId),
      values: isCheckbox
        ? field.values.map((v) => (v ? '1' : '0'))
        : field.values.map(String),
    };
  });

const isFilterEmpty = (filter?: SyncObjectFilter) => {
  return (
    !filter ||
    !Object.values(filter).some(
      (value) => !isEmpty(value) && value !== undefined
    )
  );
};
